/**
 * observability/partes.jsx — peças visuais compartilhadas pelas abas da tela
 * Observabilidade de Agentes (mesmo visual da tela de Custos: cartões, tabelas, estilos
 * inline com a paleta `C`). Os estilos são funções para relerem `C` a cada render (tema).
 */
import { C } from '@/theme/palette';
import { VAZIO } from '@/lib/format';
import { custo, inteiro, diaCurto, SEM_PERMISSAO_DE_CUSTO } from './formato';

export function estilos() {
  const th = {
    textAlign: 'left', fontSize: 12, fontWeight: 600, letterSpacing: '.04em',
    textTransform: 'uppercase', color: C.neutralMuted, padding: '8px 10px',
    borderBottom: `1px solid ${C.blackBorder}`, whiteSpace: 'nowrap', verticalAlign: 'bottom',
  };
  const td = {
    fontSize: 13.5, color: C.text, padding: '9px 10px', borderBottom: `1px solid ${C.blackBorder}`,
    fontVariantNumeric: 'tabular-nums', verticalAlign: 'top',
  };
  return {
    th,
    thNum: { ...th, textAlign: 'right' },
    td,
    tdNum: { ...td, textAlign: 'right', whiteSpace: 'nowrap' },
    tabela: { width: '100%', borderCollapse: 'collapse', background: C.blackElev, borderRadius: 10 },
    campo: {
      background: C.blackElev, color: C.text, border: `1px solid ${C.blackBorder}`,
      borderRadius: 8, padding: '6px 10px', fontSize: 13, maxWidth: '100%', minHeight: 34,
    },
    rotuloDeCampo: { color: C.neutralDim, fontSize: 12, display: 'flex', flexDirection: 'column', gap: 4 },
    botao: {
      background: 'transparent', color: C.accent, border: `1px solid ${C.blackBorder}`,
      borderRadius: 8, padding: '6px 12px', fontSize: 13, cursor: 'pointer', minHeight: 34,
    },
    link: {
      background: 'transparent', border: 'none', padding: 0, color: C.accent, cursor: 'pointer',
      font: 'inherit', textAlign: 'left', textDecoration: 'underline', textUnderlineOffset: 2,
    },
  };
}

export function Kpi({ label, value, sub, testId }) {
  return (
    <div data-testid={testId} style={{
      flex: '1 1 170px', background: C.blackElev, border: `1px solid ${C.blackBorder}`,
      borderRadius: 12, padding: '16px 18px', minWidth: 0,
    }}>
      <div style={{ fontSize: 12, letterSpacing: '.04em', textTransform: 'uppercase', color: C.neutralMuted }}>
        {label}
      </div>
      <div style={{ fontSize: 24, fontWeight: 700, color: C.text, marginTop: 6, fontVariantNumeric: 'tabular-nums', overflowWrap: 'anywhere' }}>
        {value}
      </div>
      {sub ? <div style={{ fontSize: 12.5, color: C.neutralDim, marginTop: 2 }}>{sub}</div> : null}
    </div>
  );
}

export function Titulo({ children, lado }) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', margin: '24px 0 10px' }}>
      <h3 style={{ fontSize: 13, fontWeight: 600, letterSpacing: '.06em', textTransform: 'uppercase', color: C.neutralDim, margin: 0 }}>
        {children}
      </h3>
      {lado ? <div style={{ fontSize: 12.5, color: C.neutralMuted }}>{lado}</div> : null}
    </div>
  );
}

export function Vazio({ children }) {
  return <div style={{ color: C.neutralMuted, fontSize: 14, padding: '8px 2px' }}>{children}</div>;
}

export function Nota({ children, testId }) {
  return (
    <p data-testid={testId} style={{ color: C.neutralMuted, fontSize: 12.5, lineHeight: 1.5, margin: '8px 2px 0' }}>
      {children}
    </p>
  );
}

/** Faixa no topo da aba quando a API devolve `custos_visiveis=false`. */
export function AvisoSemCusto() {
  return (
    <div role="note" data-testid="aviso-sem-custo" style={{
      background: `${C.warn}14`, border: `1px solid ${C.warn}44`, color: C.neutralDim,
      borderRadius: 10, padding: '10px 14px', fontSize: 13, marginBottom: 14,
    }}>
      <strong style={{ color: C.warn }}>{SEM_PERMISSAO_DE_CUSTO}.</strong>{' '}
      Os valores em US$ não são exibidos para o seu perfil (permissão <code>budget_costs:view</code>).
      Volume, duração, erros e reforços continuam visíveis.
    </div>
  );
}

/**
 * Valor de custo. Sem permissão → "sem permissão" (nunca zero); `completo` escreve a
 * frase inteira (cartões).
 */
export function Custo({ valor, visivel = true, completo = false }) {
  if (!visivel) {
    return (
      <span title={SEM_PERMISSAO_DE_CUSTO} aria-label={SEM_PERMISSAO_DE_CUSTO}
        style={{ color: C.neutralMuted, fontSize: completo ? 14 : 12.5, fontWeight: 400, whiteSpace: completo ? 'normal' : 'nowrap' }}>
        {completo ? SEM_PERMISSAO_DE_CUSTO : 'sem permissão'}
      </span>
    );
  }
  return <>{custo(valor)}</>;
}

export function Selo({ children, cor }) {
  const c = cor || C.neutralDim;
  return (
    <span style={{
      display: 'inline-block', fontSize: 11.5, lineHeight: 1.6, padding: '0 8px', borderRadius: 999,
      color: c, border: `1px solid ${c}55`, background: `${c}14`, whiteSpace: 'nowrap',
    }}>
      {children}
    </span>
  );
}

export function corDoStatus(status) {
  if (status === 'ok') return C.ok;
  if (status === 'erro') return C.bad;
  if (status === 'orcamento' || status === 'cancelado') return C.warn;
  return C.neutralDim;
}

/**
 * Barras por dia em SVG (uma por dia da série). O gráfico é ilustrativo: os mesmos
 * números estão na tabela "Ver os números por dia" logo abaixo.
 */
export function BarrasPorDia({ titulo, pontos = [], valorDe, formatar = inteiro, cor, testId }) {
  const valores = pontos.map((p) => Math.max(0, Number(valorDe(p)) || 0));
  const max = Math.max(0, ...valores);
  const total = valores.reduce((a, v) => a + v, 0);
  const passo = 14;
  const largura = Math.max(pontos.length * passo, 280);
  const altura = 110;
  const fill = cor || C.cyber;
  return (
    <div data-testid={testId} style={{
      flex: '1 1 380px', minWidth: 0, background: C.blackElev, border: `1px solid ${C.blackBorder}`,
      borderRadius: 12, padding: '14px 16px',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12, letterSpacing: '.04em', textTransform: 'uppercase', color: C.neutralMuted }}>{titulo}</span>
        <span style={{ fontSize: 13, color: C.neutralDim, fontVariantNumeric: 'tabular-nums' }}>
          total <strong style={{ color: C.text }}>{formatar(total)}</strong> · pico {formatar(max)}
        </span>
      </div>
      {pontos.length === 0 ? (
        <Vazio>Sem dados no período.</Vazio>
      ) : (
        <>
          <svg viewBox={`0 0 ${largura} ${altura}`} preserveAspectRatio="none" role="img"
            aria-label={`${titulo}: ${pontos.length} dia(s), total ${formatar(total)}, pico ${formatar(max)}`}
            style={{ width: '100%', height: 130, display: 'block', marginTop: 10 }}>
            <line x1="0" y1={altura - 0.5} x2={largura} y2={altura - 0.5} stroke={C.blackBorder} strokeWidth="1" />
            {pontos.map((p, i) => {
              const v = valores[i];
              const h = max > 0 ? Math.max(v > 0 ? 2 : 0, (v / max) * (altura - 8)) : 0;
              const w = (largura / pontos.length) * 0.72;
              const x = (largura / pontos.length) * i + ((largura / pontos.length) - w) / 2;
              return (
                <rect key={p.chave} x={x} y={altura - h - 1} width={w} height={h} rx="1.5" fill={fill}
                  opacity={v === max ? 1 : 0.7}>
                  <title>{`${diaCurto(p.chave)}: ${formatar(v)}`}</title>
                </rect>
              );
            })}
          </svg>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11.5, color: C.neutralMuted, marginTop: 4 }}>
            <span>{diaCurto(pontos[0].chave)}</span>
            <span>{diaCurto(pontos[pontos.length - 1].chave)}</span>
          </div>
        </>
      )}
    </div>
  );
}

/** Paginação com o total sempre à vista (a página nunca passa por total). */
export function Paginacao({ total, limit, offset, retornados, aoMudar, carregando = false }) {
  const s = estilos();
  const de = total === 0 ? 0 : offset + 1;
  const ate = offset + (retornados || 0);
  const temAnterior = offset > 0;
  const temProxima = ate < total;
  return (
    <div data-testid="paginacao" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', margin: '10px 0' }}>
      <span style={{ fontSize: 13, color: C.neutralDim, fontVariantNumeric: 'tabular-nums' }}>
        {total === 0
          ? 'Nenhuma invocação no filtro'
          : <>Mostrando <strong style={{ color: C.text }}>{inteiro(de)}–{inteiro(ate)}</strong> de <strong style={{ color: C.text }}>{inteiro(total)}</strong> invocações</>}
        {carregando ? ' · atualizando…' : ''}
      </span>
      <span style={{ display: 'flex', gap: 8 }}>
        <button type="button" style={{ ...s.botao, opacity: temAnterior ? 1 : 0.45 }} disabled={!temAnterior}
          onClick={() => aoMudar(Math.max(0, offset - limit))}>
          ← Anterior
        </button>
        <button type="button" style={{ ...s.botao, opacity: temProxima ? 1 : 0.45 }} disabled={!temProxima}
          onClick={() => aoMudar(offset + limit)}>
          Próxima →
        </button>
      </span>
    </div>
  );
}

/** Tokens de uma linha: entrada · saída · cache lido · cache criado. */
export function Tokens({ item, campos }) {
  const c = campos || ['input_tokens', 'output_tokens', 'cache_read_tokens', 'cache_creation_tokens'];
  const [e, s, cl, cc] = c.map((k) => item?.[k]);
  return (
    <div style={{ whiteSpace: 'nowrap' }}
      title={`entrada ${inteiro(e)} · saída ${inteiro(s)} · cache lido ${inteiro(cl)} · cache criado ${inteiro(cc)}`}>
      {inteiro(e)} <span style={{ color: C.neutralMuted }}>ent</span>{' · '}
      {inteiro(s)} <span style={{ color: C.neutralMuted }}>saí</span>
      <div style={{ color: C.neutralMuted, fontSize: 12 }}>
        cache: {inteiro(cl)} lido · {inteiro(cc)} criado
      </div>
    </div>
  );
}

export { VAZIO };
